import { doc, setDoc, deleteDoc, serverTimestamp, collection, onSnapshot, query, getDoc, addDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import { clearChatMessages } from './ChatService';

export const startStream = async (user) => {
    if (!user || !user.uid) return null;
    const streamRef = doc(db, 'activeStreams', user.uid);

    const streamData = {
        hostUid: user.uid,
        hostName: user.name || 'Unknown',
        photoURL: user.photoURL || null,
        startedAt: serverTimestamp(),
        lastActive: serverTimestamp(),
        viewers: 0,
        isActive: true,
        allowGuests: false, // Default to false
        // Optional: Geo-location for "Nearby" feature
        location: user.location || null
    };

    // Clear old messages before starting new stream
    await clearChatMessages(user.uid);
    await setDoc(streamRef, streamData);
    return user.uid; // Stream ID is Host UID
};

export const endStream = async (uid) => {
    if (!uid) return;
    const streamRef = doc(db, 'activeStreams', uid);
    await deleteDoc(streamRef);
    // Also clear messages on end if needed (redundant but safe)
    await clearChatMessages(uid);
};

export const updateStreamStatus = async (uid, data) => {
    if (!uid) return;
    const streamRef = doc(db, 'activeStreams', uid);
    await setDoc(streamRef, data, { merge: true });
};

export const setGuestStatus = async (uid, allowed) => {
    if (!uid) return;
    const streamRef = doc(db, 'activeStreams', uid);
    await setDoc(streamRef, { allowGuests: allowed }, { merge: true });
};

export const listenToActiveStreams = (callback) => {
    const streamsRef = collection(db, 'activeStreams');
    // Real-Sync 31.0: Only show ACTIVE streams (no stale docs)
    const q = query(streamsRef, where('isActive', '==', true));

    return onSnapshot(q, (snapshot) => {
        const streams = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
        callback(streams);
    });
};

// --- WebRTC Signaling ---

const configuration = {
    iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:stun2.l.google.com:19302' },
        { urls: 'stun:stun3.l.google.com:19302' },
        { urls: 'stun:stun4.l.google.com:19302' },
        { urls: 'stun:stun.services.mozilla.com' }
    ],
    iceCandidatePoolSize: 10,
};

/**
 * Utility: Creates an ICE Candidate Queue to handle race conditions (Real-Sync 27.0).
 * Candidates arriving before setRemoteDescription now buffer correctly.
 */
const createIceQueue = (pc) => {
    let queue = [];
    let isDescriptionSet = false;

    return {
        add: async (candidate) => {
            if (isDescriptionSet) {
                try {
                    await pc.addIceCandidate(new RTCIceCandidate(candidate));
                } catch (e) { /* Buffer failed or late candidate */ }
            } else {
                queue.push(candidate);
            }
        },
        process: async () => {
            isDescriptionSet = true;
            console.log(`ICE Queue: Processing ${queue.length} buffered candidates`);
            for (const cand of queue) {
                try {
                    await pc.addIceCandidate(new RTCIceCandidate(cand));
                } catch (e) { console.warn("ICE Queue: Buffered addition failed", e); }
            }
            queue = [];
        }
    };
};

// --- Session-Isolated Signaling Flow (Real-Sync 19.0) ---

/**
 * Viewer: Registers a unique session to join a stream.
 */
export const registerViewerSession = async (hostUid, viewerUid, sessionId) => {
    if (!hostUid || !viewerUid || !sessionId) return;
    const signalRef = doc(db, 'activeStreams', hostUid, 'signaling', sessionId);
    await setDoc(signalRef, {
        viewerUid,
        sessionId,
        status: 'joining',
        joinedAt: serverTimestamp()
    });
    return signalRef;
};

/**
 * Host: Listens for unique viewer sessions.
 */
export const listenToViewerSessions = (hostUid, callback) => {
    if (!hostUid) return () => { };
    const signalingRef = collection(db, 'activeStreams', hostUid, 'signaling');
    return onSnapshot(signalingRef, (snapshot) => {
        snapshot.docChanges().forEach(change => {
            if (change.type === 'added') {
                callback(change.doc.id, change.doc.data());
            }
        });
    });
};

/**
 * Both: RTC Room logic using a specific signal ID (Session ID).
 */
export const createRoom = async (hostUid, localStream, signalId = null) => {
    // signalId is the unique sessionId (e.g., viewerUid_timestamp)
    const signalRef = signalId
        ? doc(db, 'activeStreams', hostUid, 'signaling', signalId)
        : doc(db, 'activeStreams', hostUid);

    const peerConnection = new RTCPeerConnection(configuration);
    const iceQueue = createIceQueue(peerConnection);

    localStream.getTracks().forEach(track => {
        peerConnection.addTrack(track, localStream);
    });

    // Collect ICE candidates
    const callerCandidatesCollection = collection(signalRef, 'callerCandidates');
    peerConnection.onicecandidate = event => {
        if (!event.candidate) return;
        addDoc(callerCandidatesCollection, event.candidate.toJSON());
    };

    // Create Offer
    let offer = await peerConnection.createOffer();
    offer.sdp = setVideoBitrate(offer.sdp, 1500); // Optimized for mobile (Real-Sync 22.0)
    await peerConnection.setLocalDescription(offer);

    const roomWithOffer = {
        offer: {
            type: offer.type,
            sdp: offer.sdp,
        },
    };

    await setDoc(signalRef, roomWithOffer, { merge: true });

    // Listen for remote answer
    onSnapshot(signalRef, async snapshot => {
        const data = snapshot.data();
        if (!peerConnection.currentRemoteDescription && data && data.answer) {
            console.log('Got remote answer for session:', signalId || 'host');
            const rtcSessionDescription = new RTCSessionDescription(data.answer);
            try {
                await peerConnection.setRemoteDescription(rtcSessionDescription);
                console.log("Host: Remote description set (Answer)");
                await iceQueue.process(); // Real-Sync 27.0: Flush queued candidates
            } catch (e) {
                console.error("Host: setRemoteDescription failed:", e);
            }
        }
    });

    // Listen for remote ICE candidates (Mandatory Queuing - Real-Sync 27.0)
    const calleeCandidatesCollection = collection(signalRef, 'calleeCandidates');
    onSnapshot(calleeCandidatesCollection, snapshot => {
        snapshot.docChanges().forEach(async change => {
            if (change.type === 'added') {
                iceQueue.add(change.doc.data());
            }
        });
    });

    return peerConnection;
};

// Helper to munge SDP for higher bitrate
// Helper to munge SDP for higher bitrate (Real-Sync 28.0)
const setVideoBitrate = (sdp, kbps) => {
    const bitrate = kbps || 1500;
    const lines = sdp.split('\r\n');
    let videoSection = false;

    const modifiedLines = lines.map(line => {
        if (line.startsWith('m=video')) {
            videoSection = true;
            return line;
        }
        if (line.startsWith('m=')) {
            videoSection = false;
            return line;
        }

        if (videoSection && line.startsWith('a=fmtp:')) {
            // Target specific video VP8/H264 payload types (usually >= 96)
            if (line.includes('level-asymmetry-allowed') || /a=fmtp:\d+ /.test(line)) {
                return `${line};x-google-max-bitrate=${bitrate};x-google-min-bitrate=${Math.floor(bitrate / 4)};x-google-start-bitrate=${Math.floor(bitrate / 2)}`;
            }
        }
        return line;
    });

    // Inject b=AS line after m=video if not present
    const finalLines = [];
    modifiedLines.forEach(line => {
        finalLines.push(line);
        if (line.startsWith('m=video')) {
            finalLines.push(`b=AS:${bitrate}`);
        }
    });

    return finalLines.join('\r\n');
};

export const joinRoom = async (hostUid, onTrack, signalId = null) => {
    // Fallback to root for legacy joining
    const signalRef = signalId
        ? doc(db, 'activeStreams', hostUid, 'signaling', signalId)
        : doc(db, 'activeStreams', hostUid);

    // Viewers wait for the offer to be present in their signaling doc
    // We listen for it instead of a one-time getDoc to be more reactive
    let unsubscribeOffer = null;

    return new Promise((resolve) => {
        unsubscribeOffer = onSnapshot(signalRef, async (snapshot) => {
            const data = snapshot.data();
            if (!data || !data.offer) return;

            // Once we have an offer, we can proceed with the standard join logic
            if (unsubscribeOffer) unsubscribeOffer();

            const peerConnection = new RTCPeerConnection(configuration);
            const iceQueue = createIceQueue(peerConnection);

            peerConnection.ontrack = event => {
                console.log('Viewer: Got remote track:', event.track.kind, event.streams[0]?.id);
                // Real-Sync 30.0: Ensure we always pass a stream object to the callback
                if (event.streams && event.streams[0]) {
                    onTrack(event.streams[0]);
                } else {
                    // Fallback for non-grouped tracks
                    const inboundStream = new MediaStream();
                    inboundStream.addTrack(event.track);
                    onTrack(inboundStream);
                }
            };

            const calleeCandidatesCollection = collection(signalRef, 'calleeCandidates');
            peerConnection.onicecandidate = event => {
                if (!event.candidate) return;
                addDoc(calleeCandidatesCollection, event.candidate.toJSON());
            };

            try {
                await peerConnection.setRemoteDescription(new RTCSessionDescription(data.offer));
                console.log("Viewer: Remote description set (Offer)");
                await iceQueue.process(); // Real-Sync 27.0: Flush queued candidates
            } catch (e) {
                console.error("Viewer: setRemoteDescription failed:", e);
                // We don't throw here to avoid killing the listener, but the watchdog will catch the hang
            }

            let answer = await peerConnection.createAnswer({
                offerToReceiveVideo: true,
                offerToReceiveAudio: true
            });
            answer.sdp = setVideoBitrate(answer.sdp, 1500); // Optimized for mobile (Real-Sync 22.0)
            await peerConnection.setLocalDescription(answer);

            await setDoc(signalRef, {
                answer: { type: answer.type, sdp: answer.sdp }
            }, { merge: true });

            // --- Mandatory ICE Queuing (Real-Sync 27.0) ---
            const callerCandidatesCollection = collection(signalRef, 'callerCandidates');
            onSnapshot(callerCandidatesCollection, snapshot => {
                snapshot.docChanges().forEach(async change => {
                    if (change.type === 'added') {
                        iceQueue.add(change.doc.data());
                    }
                });
            });

            resolve(peerConnection);
        });
    });
};

// --- Multi-User Guest System ---

/**
 * Guest: Sends a request to join the host's stream.
 */
export const requestToJoin = async (hostUid, user) => {
    if (!hostUid || !user || !user.uid) return;
    const requestRef = doc(db, 'activeStreams', hostUid, 'guestRequests', user.uid);
    await setDoc(requestRef, {
        uid: user.uid,
        name: user.name || 'Anonymous',
        photoURL: user.photoURL || null,
        status: 'pending',
        requestedAt: serverTimestamp()
    });
};

/**
 * Host: Listens for guest join requests.
 */
export const listenToGuestRequests = (hostUid, callback) => {
    if (!hostUid) return () => { };
    const requestsRef = collection(db, 'activeStreams', hostUid, 'guestRequests');
    return onSnapshot(requestsRef, (snapshot) => {
        const requests = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        callback(requests);
    });
};

/**
 * Host: Responds (Accept/Decline) to a guest request.
 */
export const respondToGuestRequest = async (hostUid, guestUid, status) => {
    if (!hostUid || !guestUid) return;
    const requestRef = doc(db, 'activeStreams', hostUid, 'guestRequests', guestUid);
    if (status === 'accepted') {
        await setDoc(requestRef, { status: 'accepted' }, { merge: true });
    } else {
        await deleteDoc(requestRef);
    }
};

/**
 * Both: Listens for request status (Used by Guest to know if they were accepted).
 */
export const listenToRequestStatus = (hostUid, guestUid, callback) => {
    if (!hostUid || !guestUid) return () => { };
    const requestRef = doc(db, 'activeStreams', hostUid, 'guestRequests', guestUid);
    return onSnapshot(requestRef, (snapshot) => {
        if (snapshot.exists()) {
            callback(snapshot.data());
        } else {
            callback({ status: 'removed' });
        }
    });
};

/**
 * Host/Guest: Notify each other that guest is streaming.
 */
export const updateGuestStatus = async (hostUid, guestUid, data) => {
    const ref = doc(db, 'activeStreams', hostUid, 'activeGuests', guestUid);
    await setDoc(ref, data, { merge: true });
};

/**
 * All: Listen to active guests list.
 */
export const listenToActiveGuests = (hostUid, callback) => {
    if (!hostUid) return () => { };
    const ref = collection(db, 'activeStreams', hostUid, 'activeGuests');
    return onSnapshot(ref, (snapshot) => {
        const guests = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        callback(guests);
    });
};

/**
 * Host: Remove a guest from the stream.
 */
export const removeGuest = async (hostUid, guestUid) => {
    const requestRef = doc(db, 'activeStreams', hostUid, 'guestRequests', guestUid);
    const sessionRef = doc(db, 'activeStreams', hostUid, 'activeGuests', guestUid);
    await deleteDoc(requestRef);
    await deleteDoc(sessionRef);
};

/**
 * Both: Cleanup signaling artifacts for a specific session.
 */
export const removeSignalingRecord = async (hostUid, sessionId) => {
    if (!hostUid || !sessionId) return;
    const signalRef = doc(db, 'activeStreams', hostUid, 'signaling', sessionId);
    await deleteDoc(signalRef);
};
